import { Field, FieldRenderProps, useField } from 'react-final-form';
import { FieldArray, useFieldArray } from 'react-final-form-arrays';
import { ImageField } from '../../../../services/api/upload-vehicle';
import { NotesIcon } from '../../icons/notes';
import { useActiveNotesField } from 'services/listerUploadVehicle';
import { useDispatch } from 'react-redux';
import { ImagePlaceholderIcon } from '../../../CreateVehicle/icons/ImagePlaceholder';
import { openImageNotesDialog } from 'services/listerUploadVehicle/v2';

import { useRemoveImage } from 'pages/ListerVehicleUploadV2/hooks/useRemoveImage';
import { useBackgroundImage } from 'pages/ListerVehicleUploadV2/hooks/useBackgroundImage';
import { CircularProgress, Icon } from '@material-ui/core';
import { useVehicleEditing } from 'context/isAnotherUserEditingContext';
import * as S from './ui';
import { BinIcon, typeToIcon } from './constants';
import { NotesTopIcon } from 'components/VehicleCommon/Notes/NotesTopIcon';
import React, { useMemo, useRef } from 'react';
import { useDirectFileUpload } from 'pages/ListerVehicleUploadV2/hooks/useDirectFileUpload';
import { useHiddenUpload } from 'hooks/useHiddenUpload';
import { parsePath } from '../notes/utils';

type Props = {
  sectionId: string;
};

export const PhotosView = ({ sectionId }: Props) => {
  const { fields } = useFieldArray(`${sectionId}.images`);

  const uploadHooks = useMemo(
    () => fields.map(() => useHiddenUpload()),
    [] // ensure they are created only once
  );

  return (
    <S.Wrapper>
      <S.ImageWrapper>
        <FieldArray name={`${sectionId}.images`}>
          {({ fields }) =>
            fields.map((f, i) => (
              <Field
                localRemove={() => fields.remove(i)}
                name={f}
                key={f}
                component={props => (
                  <PhotoComponent uploadPayload={uploadHooks[i]} sectionId={sectionId} index={i} {...props} />
                )}
              />
            ))
          }
        </FieldArray>
      </S.ImageWrapper>
      <NotesComponent />
    </S.Wrapper>
  );
};

type PhotoComponentProps = FieldRenderProps<ImageField> & {
  displaySize?: 'lg' | 'md';
  withUploadLabel?: boolean;
  localRemove?: () => void;
  onClick?: () => void;
  customRemove?: () => void;
};

type EmptyImageProps = {
  type?: 'photo' | 'video';
};

type EmptyImageUploadProps = {
  sectionId: string;
} & EmptyImageProps;

export const EmptyImageWithUpload: React.FC<EmptyImageUploadProps> = ({ sectionId, type }) => {
  const { inputProps, openFileInput } = useHiddenUpload({ multiple: true, max: 25 });

  const directUpload = useDirectFileUpload();

  const {
    fields: { length: startingIndex }
  } = useFieldArray(`${sectionId}.images`, { subscription: { length: true } });

  const onFileSelected: React.ChangeEventHandler<HTMLInputElement> = e => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length === 0) return;
    directUpload(files, sectionId, startingIndex);
  };

  return (
    <>
      <div onClick={openFileInput}>
        <EmptyImage type={type} />
      </div>

      <input onChange={onFileSelected} {...inputProps} />
    </>
  );
};

export const EmptyImage: React.FC<EmptyImageProps> = ({ type = 'photo' }) => {
  return (
    <S.FieldContainer>
      <S.Image size={'md'} imageUrl={''} isPlaceholder={true}>
        <S.PlaceholderWrapper>
          {type === 'video' ? (
            <Icon style={{ color: '#757575', fontSize: 44 }}>videocam</Icon>
          ) : (
            <ImagePlaceholderIcon />
          )}
        </S.PlaceholderWrapper>
      </S.Image>
    </S.FieldContainer>
  );
};

export const PhotoComponent = ({
  input,
  localRemove = () => {},
  onClick = () => {},
  displaySize = 'lg',
  withUploadLabel = true,
  customRemove
}: PhotoComponentProps) => {
  const { hasAnotherEditor } = useVehicleEditing();

  const imageUrl = useBackgroundImage({
    localUrl: input.value.local_url,
    serverUrl: input.value.url,
    onServerLoaded: () => {
      if (input.value.local_url) {
        URL.revokeObjectURL(input.value.local_url);
        input.onChange({ ...(input.value || {}), local_url: '' });
      }
    }
  });

  const isPlaceholder = !imageUrl;

  const isUploading = input.value.status === 'uploading';
  const type = input.value.type;

  const removeImage = useRemoveImage();
  const handleRemove = async (e: any) => {
    e?.stopPropagation();
    await removeImage(input.value.id, !!type, localRemove);
  };

  const removeFn = customRemove || handleRemove;

  const { openFileInput, inputProps } = useHiddenUpload();

  const directUpload = useDirectFileUpload();
  const onFileSelected: React.ChangeEventHandler<HTMLInputElement> = e => {
    const file = e.target.files ? e.target.files[0] : null;
    if (!file) return;
    const [sectionId, index] = parsePath(input.name);
    directUpload([file], sectionId, index);
  };

  const handleClick = useMemo(() => {
    if (isUploading) return null;
    if (isPlaceholder) return openFileInput;
    return onClick;
  }, [openFileInput, onClick, isUploading, isPlaceholder]);

  return (
    <S.FieldContainer>
      {withUploadLabel && <S.UploadLabel>Upload Photos</S.UploadLabel>}
      <S.Image
        onClick={handleClick}
        size={displaySize}
        imageUrl={imageUrl}
        disabled={hasAnotherEditor}
        isPlaceholder={isPlaceholder}
      >
        <S.TopImageRow>
          {!isPlaceholder && (
            <div
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              onClick={isUploading ? undefined : removeFn}
            >
              <BinIcon />
            </div>
          )}
          <NotesIconComponent disabled={isUploading} fieldName={input.name} />
        </S.TopImageRow>
        {isPlaceholder && (
          <S.PlaceholderWrapper>{type != null ? typeToIcon[type] : <ImagePlaceholderIcon />}</S.PlaceholderWrapper>
        )}
        {input.value.label && (
          <S.Label displaySize={displaySize}>
            <span>{input.value.label}</span>
          </S.Label>
        )}
      </S.Image>
      {isUploading && (
        <S.LoadingOverlay>
          <CircularProgress />
        </S.LoadingOverlay>
      )}
      <input onChange={onFileSelected} {...inputProps} />
    </S.FieldContainer>
  );
};

type PhotoPlaceholderProps = {
  type: string;
  index: number;
};

const PhotoPlaceholder = ({ type, index }: PhotoPlaceholderProps) => {
  return <S.PlaceholderWrapper>{type != null ? typeToIcon[type] : <ImagePlaceholderIcon />}</S.PlaceholderWrapper>;
};

export const NotesIconComponent = ({ fieldName, disabled = false }: { fieldName: string; disabled?: boolean }) => {
  const dispatch = useDispatch();

  const {
    input: { value }
  } = useField(`${fieldName}.notes.message`);

  const {
    input: { value: status }
  } = useField(`${fieldName}.notes.status`);

  const hasNotes = !!value;

  const openNotesDialog = (e: any) => {
    e.stopPropagation();
    dispatch(openImageNotesDialog(fieldName));
  };

  return (
    <S.NotesIconWrapper onClick={disabled ? undefined : openNotesDialog}>
      <NotesIcon isActive={hasNotes} />
      {hasNotes && <NotesTopIcon top={2} right={2} iconType={status === 'ai_used' ? 'ai' : 'checkmark'} />}
    </S.NotesIconWrapper>
  );
};

const NotesComponent = () => {
  const activeNotesField = useActiveNotesField();
  if (!activeNotesField) return null;

  return (
    <S.NotesWrapper>
      <Field name={`${activeNotesField}.notes.message`}>{props => <span>{props.input.value}</span>}</Field>
    </S.NotesWrapper>
  );
};
